import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingsApi, type SystemSetting, type SettingUpdateItem } from '@/api/settings'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { Loader2, Settings } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { SettlementQrcodeSettings } from '@/components/settings/SettlementQrcodeSettings'
import { showError } from '@/lib/error'

// 分组标签映射
const groupLabels: Record<string, string> = {
  order: '订单设置',
  commission: '佣金分成',
  settlement: '结算设置',
  performance: '业绩设置',
  feature: '功能开关',
  business: '业务参数',
}

// 分组顺序
const groupOrder = ['order', 'commission', 'settlement', 'performance', 'feature', 'business']

// 金额类型的 number 设置（不显示 %，单位为元）
const moneyNumberKeys = new Set(['order.max_total_price'])

function SettingsForm({ settings }: { settings: SystemSetting[] }) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<Record<string, string>>({})

  // 初始化表单数据
  useEffect(() => {
    const data: Record<string, string> = {}
    settings.forEach(s => {
      data[s.key] = s.value
    })
    setFormData(data)
  }, [settings])

  const mutation = useMutation({
    mutationFn: (items: SettingUpdateItem[]) => settingsApi.batchUpdate(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('设置已保存')
    },
    onError: (error) => showError(error, '保存设置失败'),
  })

  const handleSave = () => {
    const items: SettingUpdateItem[] = []
    settings.forEach(s => {
      if (formData[s.key] !== undefined && formData[s.key] !== s.value) {
        items.push({ key: s.key, value: formData[s.key] })
      }
    })
    if (items.length === 0) {
      toast.info('没有修改需要保存')
      return
    }
    mutation.mutate(items)
  }

  return (
    <div className="space-y-4">
      {settings.map(setting => (
        <div key={setting.key} className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base">{setting.label}</Label>
            {setting.description && (
              <p className="text-sm text-muted-foreground">{setting.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {setting.type === 'boolean' ? (
              <Switch
                checked={formData[setting.key] === 'true'}
                onCheckedChange={checked =>
                  setFormData(prev => ({ ...prev, [setting.key]: String(checked) }))
                }
              />
            ) : setting.type === 'number' ? (
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min="0"
                  max={moneyNumberKeys.has(setting.key) ? undefined : '100'}
                  className="w-24 text-right"
                  value={formData[setting.key] || ''}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, [setting.key]: e.target.value }))
                  }
                />
                <span className="text-sm text-muted-foreground">
                  {moneyNumberKeys.has(setting.key) ? '元' : '%'}
                </span>
              </div>
            ) : (
              <Input
                className="w-64"
                value={formData[setting.key] || ''}
                onChange={e =>
                  setFormData(prev => ({ ...prev, [setting.key]: e.target.value }))
                }
              />
            )}
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          保存设置
        </Button>
      </div>
    </div>
  )
}

function CommissionSettingsForm({ settings }: { settings: SystemSetting[] }) {
  const queryClient = useQueryClient()
  const [lockerRate, setLockerRate] = useState('35')
  const [trackerRate, setTrackerRate] = useState('35')

  // 初始化表单数据
  useEffect(() => {
    settings.forEach(s => {
      if (s.key === 'commission.locker_rate') setLockerRate(s.value)
      if (s.key === 'commission.tracker_rate') setTrackerRate(s.value)
    })
  }, [settings])

  const locker = parseFloat(lockerRate) || 0
  const tracker = parseFloat(trackerRate) || 0
  const promoter = Math.max(0, 100 - locker - tracker)
  const total = locker + tracker + promoter
  const isValid = locker + tracker <= 100 && locker >= 0 && tracker >= 0

  const mutation = useMutation({
    mutationFn: (items: SettingUpdateItem[]) => settingsApi.batchUpdate(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('分成设置已保存')
    },
    onError: (error) => showError(error, '保存分成设置失败'),
  })

  const handleSave = () => {
    if (!isValid) {
      toast.error('锁定人 + 跟踪人比例不能超过 100%')
      return
    }
    const items: SettingUpdateItem[] = []
    settings.forEach(s => {
      if (s.key === 'commission.locker_rate' && lockerRate !== s.value) {
        items.push({ key: s.key, value: lockerRate })
      }
      if (s.key === 'commission.tracker_rate' && trackerRate !== s.value) {
        items.push({ key: s.key, value: trackerRate })
      }
    })
    if (items.length === 0) {
      toast.info('没有修改需要保存')
      return
    }
    mutation.mutate(items)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* 锁定人分成 */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base">锁定人分成</Label>
            <p className="text-sm text-muted-foreground">锁定客户的员工获得的佣金比例</p>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              max="100"
              className="w-24 text-right"
              value={lockerRate}
              onChange={e => setLockerRate(e.target.value)}
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        {/* 跟踪人分成 */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base">跟踪人分成</Label>
            <p className="text-sm text-muted-foreground">跟踪客户的员工获得的佣金比例</p>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              max="100"
              className="w-24 text-right"
              value={trackerRate}
              onChange={e => setTrackerRate(e.target.value)}
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        {/* 推广分成（只读） */}
        <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/50">
          <div className="space-y-0.5">
            <Label className="text-base">推广分成</Label>
            <p className="text-sm text-muted-foreground">自动计算 = 100% - 锁定人 - 跟踪人</p>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-2xl font-bold">{promoter.toFixed(0)}</span>
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>
      </div>

      {/* 总计进度条 */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>总计</span>
          <span className={isValid ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
            {total.toFixed(0)}%
          </span>
        </div>
        <Progress value={isValid ? 100 : total} className="h-2" />
        {!isValid && (
          <p className="text-sm text-red-600">锁定人 + 跟踪人比例不能超过 100%</p>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={mutation.isPending || !isValid}>
          {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          保存设置
        </Button>
      </div>
    </div>
  )
}

function PerformanceSettingsForm({ settings }: { settings: SystemSetting[] }) {
  const queryClient = useQueryClient()
  const [rates, setRates] = useState<Record<string, string>>({})

  // 初始化表单数据
  useEffect(() => {
    const data: Record<string, string> = {}
    settings.forEach(s => {
      data[s.key] = s.value
    })
    setRates(data)
  }, [settings])

  const promotion = parseFloat(rates['performance.promotion_rate']) || 0
  const cost = parseFloat(rates['performance.cost_rate']) || 0
  const marketing = parseFloat(rates['performance.marketing_rate']) || 0
  const warehouse = parseFloat(rates['performance.warehouse_rate']) || 0
  const creator = parseFloat(rates['performance.creator_rate']) || 0
  const tracker = parseFloat(rates['performance.tracker_rate']) || 0

  const totalDeduction = promotion + cost + marketing + warehouse
  const shareTotal = creator + tracker
  const shareValid = shareTotal === 100

  const mutation = useMutation({
    mutationFn: (items: SettingUpdateItem[]) => settingsApi.batchUpdate(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('业绩设置已保存')
    },
    onError: (error) => showError(error, '保存业绩设置失败'),
  })

  const handleSave = () => {
    if (!shareValid) {
      toast.error('创建人比例与跟踪人比例之和必须等于 100%')
      return
    }
    const items: SettingUpdateItem[] = []
    settings.forEach(s => {
      if (rates[s.key] !== undefined && rates[s.key] !== s.value) {
        items.push({ key: s.key, value: rates[s.key] })
      }
    })
    if (items.length === 0) {
      toast.info('没有修改需要保存')
      return
    }
    mutation.mutate(items)
  }

  const deductionFields = [
    { key: 'performance.promotion_rate', label: '推广扣款率', desc: '推广公司扣款比例' },
    { key: 'performance.cost_rate', label: '本金扣款率', desc: '本金扣款比例' },
    { key: 'performance.marketing_rate', label: '促销扣款率', desc: '促销扣款比例' },
    { key: 'performance.warehouse_rate', label: '仓管扣款率', desc: '仓管扣款比例' },
  ]

  return (
    <div className="space-y-6">
      {/* 扣款率设置 */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">扣款费率</h3>
        {deductionFields.map(field => (
          <div key={field.key} className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label className="text-base">{field.label}</Label>
              <p className="text-sm text-muted-foreground">{field.desc}</p>
            </div>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min="0"
                max="100"
                className="w-24 text-right"
                value={rates[field.key] ?? ''}
                onChange={e =>
                  setRates(prev => ({ ...prev, [field.key]: e.target.value }))
                }
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
        ))}

        {/* 扣款合计 */}
        <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/50">
          <div className="space-y-0.5">
            <Label className="text-base">扣款合计</Label>
            <p className="text-sm text-muted-foreground">四项扣款费率之和</p>
          </div>
          <span className="text-2xl font-bold">{totalDeduction.toFixed(0)}%</span>
        </div>
      </div>

      {/* 分成比例设置 */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">业绩分成</h3>
        {/* 创建人 */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base">创建人业绩比例</Label>
            <p className="text-sm text-muted-foreground">下单创建者获得的业绩分成（无跟踪人时自动获得 100%）</p>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              max="100"
              className="w-24 text-right"
              value={rates['performance.creator_rate'] ?? ''}
              onChange={e =>
                setRates(prev => ({ ...prev, 'performance.creator_rate': e.target.value }))
              }
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        {/* 跟踪人 */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base">跟踪人业绩比例</Label>
            <p className="text-sm text-muted-foreground">协助跟踪客户的员工获得的业绩分成</p>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              max="100"
              className="w-24 text-right"
              value={rates['performance.tracker_rate'] ?? ''}
              onChange={e =>
                setRates(prev => ({ ...prev, 'performance.tracker_rate': e.target.value }))
              }
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>
      </div>

      {/* 分成总和校验 */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>分成总和</span>
          <span className={shareValid ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
            {shareTotal.toFixed(0)}%
          </span>
        </div>
        <Progress value={shareValid ? 100 : shareTotal} className="h-2" />
        {!shareValid && (
          <p className="text-sm text-red-600">创建人比例与跟踪人比例之和必须等于 100%</p>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={mutation.isPending || !shareValid}>
          {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          保存设置
        </Button>
      </div>
    </div>
  )
}

export function SystemSettingsPage() {
  const [activeGroup, setActiveGroup] = useState('commission')

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.getSettings(),
  })

  // 按分组整理设置
  const groupedSettings = groupOrder.reduce(
    (acc, group) => {
      acc[group] = settings.filter(s => s.group === group)
      return acc
    },
    {} as Record<string, SystemSetting[]>
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Settings className="h-8 w-8" />
          系统设置
        </h1>
        <p className="text-muted-foreground mt-1">管理系统参数和业务配置</p>
      </div>

      <Tabs value={activeGroup} onValueChange={setActiveGroup}>
        <TabsList>
          {groupOrder.map(group => (
            <TabsTrigger key={group} value={group}>
              {groupLabels[group]}
            </TabsTrigger>
          ))}
        </TabsList>

        {groupOrder.map(group => (
          <TabsContent key={group} value={group}>
            <Card>
              <CardHeader>
                <CardTitle>{groupLabels[group]}</CardTitle>
              </CardHeader>
              <CardContent>
                {group === 'settlement' ? (
                  <SettlementQrcodeSettings />
                ) : groupedSettings[group]?.length > 0 ? (
                  group === 'commission' ? (
                    <CommissionSettingsForm settings={groupedSettings[group]} />
                  ) : group === 'performance' ? (
                    <PerformanceSettingsForm settings={groupedSettings[group]} />
                  ) : (
                    <SettingsForm settings={groupedSettings[group]} />
                  )
                ) : (
                  <p className="text-center text-muted-foreground py-8">暂无配置项</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
